// ============================================================
// perfilPage.js — Página de perfil de usuario
// ------------------------------------------------------------
// - Carga de datos de perfil real desde la API/local
// - Guardar datos personales reales llamando a la API
// - Cambiar contraseña llamando a la API
// ============================================================
import { qs, qsa, on } from './utils.js';
import { toast } from './toast.js';
import { getUser, saveUser, apiRequest, logoutUser } from './api.js';

export function initPerfilPage() {
  if (document.body.dataset.page !== 'perfil') return;

  // 1. Cargar datos del usuario
  const user = getUser();
  const nombreInput = qs('#perfilNombre');
  const emailInput = qs('#perfilEmail');
  const avatarName = qs('.perfil-avatar-name');
  const avatarEmail = qs('.perfil-avatar-email');
  const avatarDisplay = qs('#perfilAvatarDisplay');

  if (user) {
    if (nombreInput) nombreInput.value = user.name || '';
    if (emailInput) emailInput.value = user.email || '';
    if (avatarName) avatarName.textContent = user.name || user.email.split('@')[0];
    if (avatarEmail) avatarEmail.textContent = user.email;
    if (avatarDisplay) {
      const initial = (user.name || user.email).substring(0, 2).toUpperCase();
      avatarDisplay.textContent = initial;
    }
    
    // Mostrar plan en la tarjeta de suscripción
    const planBadge = qs('.perfil-plan-badge');
    const planName = qs('.perfil-plan-name');
    if (planBadge && planName) {
      const planCap = user.plan.charAt(0).toUpperCase() + user.plan.slice(1);
      planBadge.innerHTML = `<i class="bi bi-stars" aria-hidden="true"></i> ${planCap}`;
      planName.textContent = `Plan ${planCap}`;
    }
  }

  // 2. Foto de perfil — previsualización local (stub/upload)
  const photoBtn   = qs('[data-perfil-photo]');
  const photoInput = qs('#perfilPhotoInput');

  photoBtn?.addEventListener('click', () => photoInput?.click());

  photoInput?.addEventListener('change', (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (ev) => {
      if (avatarDisplay) avatarDisplay.innerHTML = `<img src="${ev.target.result}" alt="Foto de perfil">`;
    };
    reader.readAsDataURL(file);

    // TODO backend: subir imagen real al endpoint de /api/upload
    toast({ message: 'Foto de perfil actualizada en vista previa.', type: 'success' });
  });

  // 3. Toggle ojo en campos de contraseña
  on(document, 'click', '[data-perfil-eye]', (_e, btn) => {
    const wrap  = btn.closest('.perfil-input-wrap');
    const input = wrap?.querySelector('input');
    if (!input) return;
    const isPass = input.type === 'password';
    input.type = isPass ? 'text' : 'password';
    const icon = btn.querySelector('i');
    icon?.classList.toggle('bi-eye',       !isPass);
    icon?.classList.toggle('bi-eye-slash',  isPass);
  });

  // 4. Formulario datos personales reales
  qs('[data-perfil-form="datos"]')?.addEventListener('submit', async (e) => {
    e.preventDefault();
    const name = qs('#perfilNombre')?.value?.trim();
    if (!name) {
      toast({ message: 'El nombre es obligatorio', type: 'error' });
      return;
    }

    try {
      const data = await apiRequest('/user/me', {
        method: 'PUT',
        body: JSON.stringify({ name }),
      });
      
      saveUser(data.user);
      if (avatarName) avatarName.textContent = data.user.name;
      toast({ message: 'Datos actualizados correctamente.', type: 'success' });
    } catch (err) {
      toast({ message: err.message || 'Error al actualizar los datos.', type: 'error' });
    }
  });

  // 5. Formulario cambio de contraseña real
  qs('[data-perfil-form="password"]')?.addEventListener('submit', async (e) => {
    e.preventDefault();
    const nueva   = qs('#perfilPassNueva')?.value?.trim();
    const confirm = qs('#perfilPassConf')?.value?.trim();

    if (!nueva) {
      toast({ message: 'Introduce la nueva contraseña.', type: 'error' });
      return;
    }
    if (nueva !== confirm) {
      toast({ message: 'Las contraseñas no coinciden.', type: 'error' });
      return;
    }

    try {
      await apiRequest('/user/me', {
        method: 'PUT',
        body: JSON.stringify({ password: nueva }),
      });
      toast({ message: 'Contraseña cambiada correctamente.', type: 'success' });
      e.target.reset();
    } catch (err) {
      toast({ message: err.message || 'Error al cambiar la contraseña.', type: 'error' });
    }
  });

  // 6. Modal de cancelar suscripción
  const cancelModal = qs('#perfilCancelModal');

  qs('[data-perfil-cancel]')?.addEventListener('click', () => {
    if (cancelModal) cancelModal.hidden = false;
  });

  qsa('[data-perfil-cancel-close]').forEach((el) => {
    el.addEventListener('click', () => {
      if (cancelModal) cancelModal.hidden = true;
    });
  });

  cancelModal?.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') cancelModal.hidden = true;
  });

  qs('[data-perfil-cancel-confirm]')?.addEventListener('click', () => {
    // En un flujo real, cancelarías la suscripción de Stripe en /api/subscriptions/cancel
    cancelModal.hidden = true;
    toast({
      message: 'Suscripción cancelada. Mantendrás el acceso hasta el final del ciclo actual.',
      type: 'success',
    });
  });

  // Botón debug para simular 24h
  qs('#btnSimulate24h')?.addEventListener('click', async () => {
    try {
      const res = await apiRequest('/user/me/simulate-24h', { method: 'POST' });
      toast({ message: res.message, type: 'success' });
      
      // Hacemos una llamada a user/me para que se dispare el middleware de auto-suscripción
      setTimeout(async () => {
        const data = await apiRequest('/user/me');
        saveUser(data); // Guardar nuevos datos (donde el plan ya será profesional)
        window.location.reload();
      }, 1200);
    } catch (err) {
      toast({ message: err.message || 'Error en la simulación', type: 'error' });
    }
  });

  // 7. Cerrar sesión
  qs('[data-auth-logout]')?.addEventListener('click', async () => {
    toast({ message: 'Cerrando sesión...', type: 'success' });
    await logoutUser();
  });
}
